/**
 * TemplateManagerDialog
 *
 * Dialog for CRUD operations on event templates.
 * Allows creating, editing, and deleting reusable event blueprints.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Pencil, Trash2, Plus, Loader2, X } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import type {
  CalendarEventTemplateDetail,
  CalendarEventTemplateInput,
  CalendarEventReminder,
} from '@/lib/api';
import type { CalendarCollection } from '../types';
import { REMINDER_PRESETS, PRESET_COLORS } from '../types';
import { ColorPicker } from './ColorPicker';

interface TemplateManagerDialogProps {
  open: boolean;
  onClose: () => void;
  templates: CalendarEventTemplateDetail[];
  calendars: CalendarCollection[];
  onCreateTemplate: (input: CalendarEventTemplateInput) => Promise<unknown>;
  onUpdateTemplate: (id: string, input: Partial<CalendarEventTemplateInput>) => Promise<unknown>;
  onDeleteTemplate: (id: string) => Promise<void>;
}

type Mode = 'list' | 'create' | 'edit';

export function TemplateManagerDialog({
  open,
  onClose,
  templates,
  calendars,
  onCreateTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
}: TemplateManagerDialogProps) {
  const [mode, setMode] = useState<Mode>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [isAllDay, setIsAllDay] = useState(false);
  const [status, setStatus] = useState<'confirmed' | 'tentative' | 'cancelled'>('confirmed');
  const [color, setColor] = useState<string | null>(null);
  const [showColor, setShowColor] = useState(false);
  const [url, setUrl] = useState('');
  const [calendarId, setCalendarId] = useState('');
  const [tags, setTags] = useState('');
  const [reminders, setReminders] = useState<CalendarEventReminder[]>([
    { type: 'notification', minutesBefore: 15 },
  ]);

  // Reset form when switching modes
  useEffect(() => {
    if (!open) {
      setMode('list');
      setEditingId(null);
    }
  }, [open]);

  const resetForm = () => {
    setName('');
    setTitle('');
    setDescription('');
    setLocation('');
    setDurationMinutes(60);
    setIsAllDay(false);
    setStatus('confirmed');
    setColor(null);
    setShowColor(false);
    setUrl('');
    setCalendarId('');
    setTags('');
    setReminders([{ type: 'notification', minutesBefore: 15 }]);
  };

  const populateForm = (template: CalendarEventTemplateDetail) => {
    setName(template.name);
    setTitle(template.title);
    setDescription(template.description || '');
    setLocation(template.location || '');
    setDurationMinutes(template.durationMinutes);
    setIsAllDay(template.isAllDay);
    setStatus(template.status);
    setColor(template.color);
    setShowColor(!!template.color);
    setUrl(template.url || '');
    setCalendarId(template.calendarId || '');
    setTags(template.tags?.join(', ') || '');
    setReminders(
      (template.reminders as CalendarEventReminder[]) || [
        { type: 'notification', minutesBefore: 15 },
      ],
    );
  };

  const handleStartCreate = () => {
    resetForm();
    setMode('create');
  };

  const handleStartEdit = (template: CalendarEventTemplateDetail) => {
    populateForm(template);
    setEditingId(template.id);
    setMode('edit');
  };

  const buildInput = (): CalendarEventTemplateInput => ({
    name: name.trim(),
    title: title.trim(),
    description: description || undefined,
    location: location || undefined,
    durationMinutes,
    isAllDay,
    status,
    color: showColor ? color || undefined : undefined,
    url: url || undefined,
    calendarId: calendarId || undefined,
    reminders,
    tags: tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean),
  });

  const handleSave = async () => {
    if (!name.trim() || !title.trim()) {
      toast({ title: 'Name and title are required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      if (mode === 'edit' && editingId) {
        await onUpdateTemplate(editingId, buildInput());
        toast({ title: 'Template updated' });
      } else {
        await onCreateTemplate(buildInput());
        toast({ title: 'Template created' });
      }
      setMode('list');
      setEditingId(null);
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to save template',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await onDeleteTemplate(id);
      toast({ title: 'Template deleted' });
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to delete template',
        variant: 'destructive',
      });
    }
  };

  const addReminder = () => {
    setReminders((prev) => [...prev, { type: 'notification', minutesBefore: 15 }]);
  };

  const removeReminder = (index: number) => {
    setReminders((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'list'
              ? 'Event Templates'
              : mode === 'create'
                ? 'New Template'
                : 'Edit Template'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'list'
              ? 'Reusable event blueprints for quick event creation.'
              : 'Define the template details.'}
          </DialogDescription>
        </DialogHeader>

        {mode === 'list' ? (
          <>
            <div className="space-y-2">
              {templates.map((t) => (
                <div key={t.id} className="flex items-center gap-2 border rounded-md px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{t.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {t.title} &middot; {t.durationMinutes}min
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleStartEdit(t)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive"
                    onClick={() => handleDelete(t.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              {templates.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No templates yet. Create one to get started.
                </p>
              )}
            </div>
            <Button onClick={handleStartCreate} className="w-full">
              <Plus className="h-4 w-4 mr-1" />
              New Template
            </Button>
          </>
        ) : (
          <>
            <div className="space-y-3 py-1">
              <div>
                <Label className="text-xs">Template Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Weekly Standup"
                  className="text-sm"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs">Event Title</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Event title"
                  className="text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Duration (minutes)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(parseInt(e.target.value, 10) || 60)}
                    className="text-sm"
                  />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={isAllDay} onCheckedChange={(c) => setIsAllDay(c === true)} />
                    <span className="text-sm">All day</span>
                  </label>
                </div>
              </div>
              <div>
                <Label className="text-xs">Location</Label>
                <Input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Add location"
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add description"
                  rows={2}
                  className="text-sm"
                />
              </div>
              {calendars.length > 0 && (
                <div>
                  <Label className="text-xs">Default Calendar</Label>
                  <Select value={calendarId} onValueChange={setCalendarId}>
                    <SelectTrigger className="text-sm">
                      <SelectValue placeholder="Any calendar" />
                    </SelectTrigger>
                    <SelectContent>
                      {calendars.map((cal) => (
                        <SelectItem key={cal.id} value={cal.id}>
                          <span className="flex items-center gap-2">
                            <span
                              className="w-2.5 h-2.5 rounded-full inline-block"
                              style={{ backgroundColor: cal.color }}
                            />
                            {cal.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
                  <SelectTrigger className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="tentative">Tentative</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Reminders */}
              <div>
                <Label className="text-xs">Reminders</Label>
                <div className="space-y-2 mt-1">
                  {reminders.map((rem, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Select
                        value={String(rem.minutesBefore)}
                        onValueChange={(v) => {
                          const next = [...reminders];
                          next[i] = { ...next[i], minutesBefore: parseInt(v, 10) };
                          setReminders(next);
                        }}
                      >
                        <SelectTrigger className="text-xs h-8 flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {REMINDER_PRESETS.map((p) => (
                            <SelectItem key={p.minutes} value={String(p.minutes)}>
                              {p.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => removeReminder(i)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addReminder} className="text-xs">
                    Add reminder
                  </Button>
                </div>
              </div>
              {/* Color */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <Checkbox
                    checked={showColor}
                    onCheckedChange={(c) => {
                      setShowColor(c === true);
                      if (!c) setColor(null);
                    }}
                  />
                  <span className="text-xs">Custom color</span>
                </label>
                {showColor && <ColorPicker value={color || PRESET_COLORS[0]} onChange={setColor} />}
              </div>
              {/* URL */}
              <div>
                <Label className="text-xs">URL</Label>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://..."
                  className="text-sm"
                />
              </div>
              {/* Tags */}
              <div>
                <Label className="text-xs">Tags (comma-separated)</Label>
                <Input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="meeting, standup, ..."
                  className="text-sm"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setMode('list');
                  setEditingId(null);
                }}
                disabled={saving}
              >
                Back
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                {mode === 'edit' ? 'Save Changes' : 'Create Template'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
